// Vendors (plan v2 §3.1): who answers the calls the portal makes and who sends us webhooks. Channels (Exotel,
// Pingbix, Meta), lenders (the Lender Hub) and Firstfund's own services (lead registry, CMP). A vendor's key is
// typed once, kept in the vault, and only its first 6 characters are ever shown back.
import { z } from 'zod'
import { Channel, Id, IsoDate, IsoDateTime } from './common'
import { ApiHealth, VendorApiRow, VendorKind } from './vendor-apis'
import { BaseUrl } from './vendor-changes'

const count = z.number().int().min(0)

export const VendorStatus = z.enum(['onboarded', 'not_onboarded'])
export type VendorStatus = z.infer<typeof VendorStatus>

/** live: real people are reached through it, so a change waits for a second person; test: UAT / sandbox. */
export const VendorEnvironment = z.enum(['live', 'test'])
export type VendorEnvironment = z.infer<typeof VendorEnvironment>

export const Vendor = z.object({
  id: Id,
  name: z.string(),
  kind: VendorKind,
  status: VendorStatus,
  environment: VendorEnvironment,
  read_only: z.boolean().describe('we only read from it (Meta: quality, limits, template status)'),
  channels: z.array(Channel),
  account: z.string(),
  base_url: z.string().describe('every API path of this vendor starts here'),
  key_prefix: z.string().describe('first 6 characters + "…"; the key itself never leaves the vault'),
  callback_url: z.string().describe('what the vendor calls with delivery reports, replies and digits'),
  since: IsoDate.nullable(),
  note: z.string(),
  health: ApiHealth.describe('the worst status of its APIs'),
  apis: count,
  calls_today: count,
  errors_today: count,
  last_call_at: IsoDateTime.nullable(),
})
export type Vendor = z.infer<typeof Vendor>

/** A vendor's page: the vendor and one row per API we call and per webhook we receive. */
export const VendorDetail = z.object({ vendor: Vendor, apis: z.array(VendorApiRow) })
export type VendorDetail = z.infer<typeof VendorDetail>

export const AddVendorInput = z.object({
  name: z.string().trim().min(2, 'Type the vendor name'),
  account: z.string().trim(),
  base_url: z.union([z.literal(''), BaseUrl]),
  channels: z.array(Channel).min(1, 'Pick at least one channel'),
  note: z.string().trim().max(200),
})
export type AddVendorInput = z.infer<typeof AddVendorInput>

export const OnboardVendorInput = z.object({
  account: z.string().trim().min(1, 'Type the account or SID'),
  api_key: z.string().trim().min(8, 'The key has at least 8 characters'),
})
export type OnboardVendorInput = z.infer<typeof OnboardVendorInput>

export interface VendorsApi {
  listVendors(): Promise<Vendor[]>
  getVendor(id: string): Promise<VendorDetail>
  addVendor(input: AddVendorInput): Promise<Vendor>
  onboardVendor(id: string, input: OnboardVendorInput): Promise<Vendor>
  switchOffVendor(id: string): Promise<Vendor>
}
